import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ComercialLayout } from '../../components/comercial/ComercialLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from '../../components/ui/sonner';
import axios from 'axios';
import {
  Building2,
  Home,
  User,
  Phone,
  Mail,
  MapPin,
  Briefcase,
  Users,
  MessageSquare,
  Calendar,
  CheckCircle,
  Loader2,
  Droplets,
  LeafIcon,
  Banknote,
  BadgeCheck,
  HelpCircle,
  Share2,
  CreditCard,
  FileText,
  Wrench
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Clases estáticas para Tailwind (evita interpolación dinámica)
const INPUT_CLASS_BASE = 'bg-white/[0.03] border-white/10 text-white placeholder:text-zinc-600';
const INPUT_CLASS_HOGAR = `${INPUT_CLASS_BASE} focus:border-sky-500/50`;
const INPUT_CLASS_EMPRESA = `${INPUT_CLASS_BASE} focus:border-amber-500/50`;

// Equipment options - MISMO catálogo para Hogar y Empresa
const EQUIPMENT_OPTIONS = [
  { value: 'TIKAL', label: 'TIKAL', desc: 'Agua fría + caliente + tiempo', capacity: '3.5L' },
  { value: 'TIKAL_S', label: 'TIKAL S', desc: 'Compacto, fría + caliente + tiempo', capacity: '1.7L' },
  { value: 'CHAAK', label: 'CHAAK', desc: 'Alta capacidad', capacity: '8L' },
  { value: 'KAJAAL', label: 'KAJAAL', desc: 'Solo agua del tiempo', capacity: null },
  { value: 'MAASKAB', label: 'MÁASKAB', desc: 'Modelo premium', capacity: null },
  { value: 'pendiente_asesoramiento', label: 'Pendiente de asesoramiento', desc: 'Decidir después de visita', capacity: null },
];

// Business type options - Solo para Empresa
const BUSINESS_TYPES = [
  { value: 'oficina', label: 'Oficina' },
  { value: 'restaurante', label: 'Restaurante' },
  { value: 'hotel', label: 'Hotel' },
  { value: 'clinica', label: 'Clínica / Centro de salud' },
  { value: 'gimnasio', label: 'Gimnasio' },
  { value: 'comercio', label: 'Comercio' },
  { value: 'industria', label: 'Industria' },
  { value: 'educacion', label: 'Centro educativo' },
  { value: 'otro', label: 'Otro' },
];

// Detected needs
const NEEDS_OPTIONS = [
  { value: 'agua_empleados', label: 'Agua para empleados', icon: Users },
  { value: 'agua_clientes', label: 'Agua para clientes', icon: Droplets },
  { value: 'sustituir_garrafas', label: 'Sustituir garrafas', icon: BadgeCheck },
  { value: 'reducir_costes', label: 'Reducir costes', icon: Banknote },
  { value: 'imagen_ecologica', label: 'Imagen ecológica', icon: LeafIcon },
  { value: 'otro', label: 'Otro motivo', icon: HelpCircle },
];

// Origen del cliente options
const ORIGEN_OPTIONS = [
  { value: 'referido', label: 'Referido', desc: 'Cliente existente lo recomendó' },
  { value: 'puerta_fria', label: 'Puerta Fría', desc: 'Visita comercial directa' },
  { value: 'web', label: 'Web', desc: 'Formulario de la web' },
  { value: 'google_ads', label: 'Google Ads', desc: 'Campaña de Google' },
  { value: 'meta_ads', label: 'Meta Ads', desc: 'Campaña Facebook/Instagram' },
  { value: 'instagram', label: 'Instagram', desc: 'Orgánico Instagram' },
  { value: 'facebook', label: 'Facebook', desc: 'Orgánico Facebook' },
  { value: 'llamada', label: 'Llamada', desc: 'Llamada telefónica' },
  { value: 'otros', label: 'Otros', desc: 'Otro origen' },
];

// Form Section Component
function FormSection({ title, children }) {
  return (
    <div className="space-y-4">
      <h3 className="text-xs uppercase tracking-wider text-zinc-500 font-medium font-['Manrope']">{title}</h3>
      {children}
    </div>
  );
}

// Input Field Component
function FormField({ icon: Icon, label, required, children }) {
  return (
    <div>
      <label className="flex items-center gap-2 text-sm text-zinc-400 mb-2 font-['Manrope']">
        {Icon && <Icon className="w-4 h-4" />}
        {label}
        {required && <span className="text-red-400">*</span>}
      </label>
      {children}
    </div>
  );
}

export default function NuevaPrueba() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [tecnicos, setTecnicos] = useState([]);

  // Sin técnico asignado la prueba sólo la ven los técnicos con Vista de Equipo
  useEffect(() => {
    axios.get(`${API_URL}/api/comercial/tecnicos`, { withCredentials: true })
      .then((r) => setTecnicos(r.data || []))
      .catch((err) => console.error('Error fetching tecnicos:', err));
  }, []);
  
  // Form state
  const [formData, setFormData] = useState({
    tipo_cliente: 'hogar',
    nombre_contacto: '',
    telefono: '',
    email: '',
    direccion: '',
    codigo_postal: '',
    ciudad: '',
    observaciones: '',
    necesidades_detectadas: [],
    equipo_recomendado: 'pendiente_asesoramiento',
    fecha_prevista: '',
    hora_prevista: '',
    tecnico_id: '',
    origen_cliente: 'otros',
    referido_por: '',
    empresa: '',
    tipo_negocio: '',
    num_empleados: '',
    cif: '',
    persona_contacto_cargo: '',
    direccion_fiscal: '',
    nif: '',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNeedToggle = (need) => {
    setFormData(prev => ({
      ...prev,
      necesidades_detectadas: prev.necesidades_detectadas.includes(need)
        ? prev.necesidades_detectadas.filter(n => n !== need)
        : [...prev.necesidades_detectadas, need]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const commonRequired = ['nombre_contacto', 'telefono', 'email', 'direccion', 'codigo_postal', 'ciudad'];
    const empresaRequired = ['empresa', 'tipo_negocio'];
    
    let required = [...commonRequired];
    if (formData.tipo_cliente === 'empresa') {
      required = [...required, ...empresaRequired];
    }
    
    const missing = required.filter(field => !formData[field]);
    
    if (missing.length > 0) {
      toast.error('Por favor completa todos los campos obligatorios');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('El email no tiene un formato válido');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/comercial/trials`, {
        ...formData,
        nif: formData.nif || formData.cif || null,
        tecnico_id: formData.tecnico_id || null,
      }, { withCredentials: true });
      toast.success('Prueba gratuita creada correctamente');
      navigate('/comercial/crm');
    } catch (err) {
      console.error('Error creating trial:', err);
      toast.error(err.response?.data?.detail || 'Error al crear la prueba gratuita');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const today = new Date().toISOString().split('T')[0];
  const isEmpresa = formData.tipo_cliente === 'empresa';
  const inputClass = isEmpresa ? INPUT_CLASS_EMPRESA : INPUT_CLASS_HOGAR;

  return (
    <ComercialLayout
      title="Nueva Prueba Gratuita"
      subtitle="Registra un nuevo cliente potencial"
      onLogout={handleLogout}
    >
      <form onSubmit={handleSubmit} className="max-w-4xl">
        {/* Quick info banner */}
        <div className="mb-6 p-4 rounded-xl bg-emerald-500/[0.08] border border-emerald-500/20">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
            <p className="text-sm text-zinc-300 font-['Manrope']">
              Completa los datos del cliente para registrar una nueva prueba gratuita. 
              Los campos marcados con <span className="text-red-400">*</span> son obligatorios.
            </p>
          </div>
        </div>

        {/* Tipo de Cliente Selector */}
        <div className="mb-8">
          <FormSection title="Tipo de Cliente">
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => handleChange('tipo_cliente', 'hogar')}
                className={`p-6 rounded-2xl border-2 transition-all flex flex-col items-center gap-3 ${
                  !isEmpresa 
                    ? 'bg-sky-500/20 border-sky-500/50 text-sky-300' 
                    : 'bg-white/[0.02] border-white/10 text-zinc-400 hover:border-white/20'
                }`}
                data-testid="tipo-hogar"
              >
                <Home className={`w-10 h-10 ${!isEmpresa ? 'text-sky-400' : 'text-zinc-500'}`} />
                <div className="text-center">
                  <p className={`font-medium ${!isEmpresa ? 'text-sky-300' : 'text-white'}`}>Hogar</p>
                  <p className="text-xs text-zinc-500 mt-1">Cliente particular</p>
                </div>
              </button>
              
              <button
                type="button"
                onClick={() => handleChange('tipo_cliente', 'empresa')}
                className={`p-6 rounded-2xl border-2 transition-all flex flex-col items-center gap-3 ${
                  isEmpresa 
                    ? 'bg-amber-500/20 border-amber-500/50 text-amber-300' 
                    : 'bg-white/[0.02] border-white/10 text-zinc-400 hover:border-white/20'
                }`}
                data-testid="tipo-empresa"
              >
                <Building2 className={`w-10 h-10 ${isEmpresa ? 'text-amber-400' : 'text-zinc-500'}`} />
                <div className="text-center">
                  <p className={`font-medium ${isEmpresa ? 'text-amber-300' : 'text-white'}`}>Empresa</p>
                  <p className="text-xs text-zinc-500 mt-1">Negocio o empresa</p>
                </div>
              </button>
            </div>
          </FormSection>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Client Data */}
          <div className="space-y-6">
            <FormSection title={isEmpresa ? "Datos de la Empresa" : "Datos del Cliente"}>
              {isEmpresa && (
                <>
                  <FormField icon={Building2} label="Razón Social" required>
                    <Input
                      value={formData.empresa}
                      onChange={(e) => handleChange('empresa', e.target.value)}
                      placeholder="Nombre de la empresa"
                      className={INPUT_CLASS_EMPRESA}
                      data-testid="trial-empresa"
                    />
                  </FormField>

                  <FormField icon={FileText} label="CIF">
                    <Input
                      value={formData.cif}
                      onChange={(e) => handleChange('cif', e.target.value.toUpperCase())}
                      placeholder="B12345678"
                      maxLength={9}
                      className={INPUT_CLASS_EMPRESA}
                      data-testid="trial-cif"
                    />
                  </FormField>
                </>
              )}

              {!isEmpresa && (
                <FormField icon={FileText} label="NIF">
                  <Input
                    value={formData.nif}
                    onChange={(e) => handleChange('nif', e.target.value.toUpperCase())}
                    placeholder="12345678A"
                    maxLength={9}
                    className={INPUT_CLASS_HOGAR}
                    data-testid="trial-nif"
                  />
                </FormField>
              )}

              <FormField icon={User} label={isEmpresa ? "Persona de contacto" : "Nombre completo"} required>
                <Input
                  value={formData.nombre_contacto}
                  onChange={(e) => handleChange('nombre_contacto', e.target.value)}
                  placeholder="Nombre y apellidos"
                  className={inputClass}
                  data-testid="trial-contacto"
                />
              </FormField>

              {isEmpresa && (
                <FormField icon={Briefcase} label="Cargo">
                  <Input
                    value={formData.persona_contacto_cargo}
                    onChange={(e) => handleChange('persona_contacto_cargo', e.target.value)}
                    placeholder="Director, Gerente, Responsable..."
                    className={INPUT_CLASS_EMPRESA}
                    data-testid="trial-cargo"
                  />
                </FormField>
              )}

              <div className="grid grid-cols-2 gap-4">
                <FormField icon={Phone} label="Teléfono" required>
                  <Input
                    type="tel"
                    value={formData.telefono}
                    onChange={(e) => handleChange('telefono', e.target.value)}
                    placeholder="+34 600 000 000"
                    className={inputClass}
                    data-testid="trial-telefono"
                  />
                </FormField>

                <FormField icon={Mail} label="Email" required>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder={isEmpresa ? "contacto@empresa.com" : "cliente@email.com"}
                    className={inputClass}
                    data-testid="trial-email"
                  />
                </FormField>
              </div>

              <FormField icon={MapPin} label={isEmpresa ? "Dirección de instalación" : "Dirección"} required>
                <Input
                  value={formData.direccion}
                  onChange={(e) => handleChange('direccion', e.target.value)}
                  placeholder="Calle, número, piso..."
                  className={inputClass}
                  data-testid="trial-direccion"
                />
              </FormField>

              <div className="grid grid-cols-2 gap-4">
                <FormField label="Código Postal" required>
                  <Input
                    value={formData.codigo_postal}
                    onChange={(e) => handleChange('codigo_postal', e.target.value)}
                    placeholder="28001"
                    maxLength={5}
                    className={inputClass}
                    data-testid="trial-cp"
                  />
                </FormField>

                <FormField label="Ciudad" required>
                  <Input
                    value={formData.ciudad}
                    onChange={(e) => handleChange('ciudad', e.target.value)}
                    placeholder="Madrid"
                    className={inputClass}
                    data-testid="trial-ciudad"
                  />
                </FormField>
              </div>

              {isEmpresa && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <FormField icon={Briefcase} label="Tipo de negocio" required>
                      <select
                        value={formData.tipo_negocio}
                        onChange={(e) => handleChange('tipo_negocio', e.target.value)}
                        className="w-full h-10 px-3 rounded-md bg-white/[0.03] border border-white/10 text-white focus:border-amber-500/50 focus:outline-none font-['Manrope']"
                        data-testid="trial-tipo-negocio"
                      >
                        <option value="" className="bg-zinc-900">Seleccionar...</option>
                        {BUSINESS_TYPES.map(type => (
                          <option key={type.value} value={type.value} className="bg-zinc-900">
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </FormField>

                    <FormField icon={Users} label="Nº empleados aprox.">
                      <Input
                        value={formData.num_empleados}
                        onChange={(e) => handleChange('num_empleados', e.target.value)}
                        placeholder="10-20"
                        className={INPUT_CLASS_EMPRESA}
                        data-testid="trial-empleados"
                      />
                    </FormField>
                  </div>

                  <FormField icon={CreditCard} label="Dirección fiscal (si diferente)">
                    <Input
                      value={formData.direccion_fiscal}
                      onChange={(e) => handleChange('direccion_fiscal', e.target.value)}
                      placeholder="Dejar vacío si es la misma dirección"
                      className={INPUT_CLASS_EMPRESA}
                      data-testid="trial-direccion-fiscal"
                    />
                  </FormField>
                </>
              )}
            </FormSection>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            <FormSection title="Origen del cliente">
              <div className="grid grid-cols-3 gap-2">
                {ORIGEN_OPTIONS.map(origen => {
                  const isSelected = formData.origen_cliente === origen.value;
                  return (
                    <button
                      key={origen.value}
                      type="button"
                      onClick={() => handleChange('origen_cliente', origen.value)}
                      className={`p-3 rounded-xl border text-left transition-all ${
                        isSelected 
                          ? (isEmpresa ? 'bg-amber-500/20 border-amber-500/50 text-amber-300' : 'bg-sky-500/20 border-sky-500/50 text-sky-300')
                          : 'bg-white/[0.02] border-white/10 text-zinc-400 hover:border-white/20'
                      }`}
                      data-testid={`origen-${origen.value}`}
                    >
                      <span className="text-xs font-medium font-['Manrope']">{origen.label}</span>
                    </button>
                  );
                })}
              </div>
              {formData.origen_cliente === 'referido' && (
                <div className="mt-3">
                  <FormField icon={Share2} label="¿Quién lo refirió?">
                    <Input
                      value={formData.referido_por}
                      onChange={(e) => handleChange('referido_por', e.target.value)}
                      placeholder="Nombre del cliente que lo refirió"
                      className={inputClass}
                      data-testid="trial-referido-por"
                    />
                  </FormField>
                </div>
              )}
            </FormSection>

            <FormSection title="Necesidades detectadas">
              <div className="grid grid-cols-2 gap-2">
                {NEEDS_OPTIONS.map(need => {
                  const Icon = need.icon;
                  const isSelected = formData.necesidades_detectadas.includes(need.value);
                  return (
                    <button
                      key={need.value}
                      type="button"
                      onClick={() => handleNeedToggle(need.value)}
                      className={`p-3 rounded-xl border text-left transition-all flex items-center gap-2 ${
                        isSelected 
                          ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-300' 
                          : 'bg-white/[0.02] border-white/10 text-zinc-400 hover:border-white/20'
                      }`}
                      data-testid={`need-${need.value}`}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      <span className="text-xs font-['Manrope']">{need.label}</span>
                    </button>
                  );
                })}
              </div>
            </FormSection>

            <FormSection title="Equipo recomendado">
              <div className="space-y-2">
                {EQUIPMENT_OPTIONS.map(eq => {
                  const isSelected = formData.equipo_recomendado === eq.value;
                  return (
                    <label
                      key={eq.value}
                      className={`flex items-center p-3 rounded-xl border cursor-pointer transition-all ${
                        isSelected
                          ? (isEmpresa ? 'bg-amber-500/20 border-amber-500/50' : 'bg-sky-500/20 border-sky-500/50')
                          : 'bg-white/[0.02] border-white/10 hover:border-white/20'
                      }`}
                      data-testid={`equipo-${eq.value}`}
                    >
                      <input
                        type="radio"
                        name="equipo"
                        value={eq.value}
                        checked={isSelected}
                        onChange={(e) => handleChange('equipo_recomendado', e.target.value)}
                        className="sr-only"
                      />
                      <div className="flex-1">
                        <p className={`text-sm font-medium ${isSelected ? (isEmpresa ? 'text-amber-300' : 'text-sky-300') : 'text-white'}`}>
                          {eq.label}
                        </p>
                        <p className="text-xs text-zinc-500 font-['Manrope']">{eq.desc}</p>
                      </div>
                      {eq.capacity && (
                        <span className="text-xs text-zinc-500 font-['Manrope']">{eq.capacity}</span>
                      )}
                    </label>
                  );
                })}
              </div>
            </FormSection>

            <FormSection title="Programación">
              <div className="grid grid-cols-2 gap-4">
                <FormField icon={Calendar} label="Fecha prevista instalación">
                  <Input
                    type="date"
                    value={formData.fecha_prevista}
                    onChange={(e) => handleChange('fecha_prevista', e.target.value)}
                    min={today}
                    className={`bg-white/[0.03] border-white/10 text-white ${isEmpresa ? 'focus:border-amber-500/50' : 'focus:border-sky-500/50'}`}
                    data-testid="trial-fecha"
                  />
                </FormField>

                <FormField label="Hora prevista">
                  <Input
                    type="time"
                    value={formData.hora_prevista}
                    onChange={(e) => handleChange('hora_prevista', e.target.value)}
                    className={`bg-white/[0.03] border-white/10 text-white ${isEmpresa ? 'focus:border-amber-500/50' : 'focus:border-sky-500/50'}`}
                    data-testid="trial-hora"
                  />
                </FormField>
              </div>

              <FormField icon={Wrench} label="Técnico que instala la prueba">
                <select
                  value={formData.tecnico_id}
                  onChange={(e) => handleChange('tecnico_id', e.target.value)}
                  className={`w-full h-10 px-3 rounded-md bg-white/[0.03] border border-white/10 text-white text-sm ${isEmpresa ? 'focus:border-amber-500/50' : 'focus:border-sky-500/50'} focus:outline-none`}
                  data-testid="trial-tecnico"
                >
                  <option value="" className="bg-zinc-900">Sin asignar</option>
                  {tecnicos.map((t) => (
                    <option key={t.id} value={t.id} className="bg-zinc-900">{t.name}</option>
                  ))}
                </select>
              </FormField>
            </FormSection>

            <FormSection title="Observaciones">
              <FormField icon={MessageSquare} label="Notas adicionales">
                <textarea
                  value={formData.observaciones}
                  onChange={(e) => handleChange('observaciones', e.target.value)}
                  placeholder="Información relevante sobre el cliente, horarios de contacto, persona de referencia..."
                  rows={3}
                  className={`w-full px-3 py-2 rounded-md bg-white/[0.03] border border-white/10 text-white placeholder:text-zinc-600 ${isEmpresa ? 'focus:border-amber-500/50' : 'focus:border-sky-500/50'} focus:outline-none resize-none font-['Manrope']`}
                  data-testid="trial-observaciones"
                />
              </FormField>
            </FormSection>
          </div>
        </div>

        {/* Submit Button */}
        <div className="mt-8 flex items-center justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/comercial')}
            className="border-white/20 text-zinc-400 hover:text-white"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className={`${isEmpresa ? 'bg-amber-500 hover:bg-amber-600 shadow-amber-500/20' : 'bg-sky-500 hover:bg-sky-600 shadow-sky-500/20'} text-white min-w-[200px] shadow-lg`}
            data-testid="trial-submit"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Guardando...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Crear Prueba Gratuita
              </>
            )}
          </Button>
        </div>
      </form>
    </ComercialLayout>
  );
}
